/* TourStock trade enquiry: decides whether something a visitor typed is a close match to a catalogue product. */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.TsTradeMatch = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var STOP = {};
  (
    "a an and or the for with of to in on at by from x pc pcs piece pieces pack packs box boxes roll rolls unit units " +
    "qty quantity no need needs want wants please some any new set sets"
  )
    .split(" ")
    .forEach(function (w) {
      STOP[w] = true;
    });

  var COLOURS = {};
  "black white red blue green yellow orange pink purple grey gray silver gold brown clear natural cyan magenta"
    .split(" ")
    .forEach(function (w) {
      COLOURS[w] = true;
    });

  var UNIT = "mm|cm|m|v|a|ah|mah|w|kw|kg|g|ml|l|in|ft|hz|khz|ohm|db|amp|amps";
  var DIMENSION = new RegExp("^\\d+(?:\\.\\d+)?(?:" + UNIT + ")$");

  function normalise(text) {
    var s = String(text || "")
      .toLowerCase()
      .replace(/[®™©]/g, " ")
      .replace(/×/g, " x ")
      .replace(/[”″"]/g, " in ")
      .replace(/\b(metres?|meters?)\b/g, "m")
      .replace(/\b(millimet(?:re|er)s?)\b/g, "mm")
      .replace(/\b(centimet(?:re|er)s?)\b/g, "cm")
      .replace(/\b(volts?)\b/g, "v")
      .replace(/\b(inch|inches)\b/g, "in");
    s = s.replace(new RegExp("(\\d(?:\\.\\d+)?)\\s*(" + UNIT + ")\\b", "g"), "$1$2");
    return s
      .split(/[^a-z0-9.]+/)
      .map(function (t) {
        return t.replace(/^\.+|\.+$/g, "");
      })
      .filter(Boolean);
  }

  function stem(token) {
    if (/^\d/.test(token) || token.length < 4) return token;
    if (/ies$/.test(token)) return token.slice(0, -3) + "y";
    if (/(ch|sh|x|s|z)es$/.test(token)) return token.slice(0, -2);
    if (/[^s]s$/.test(token)) return token.slice(0, -1);
    return token;
  }

  function significant(tokens) {
    return tokens.filter(function (t) {
      return !STOP[t];
    });
  }

  function isHard(token) {
    return (
      DIMENSION.test(token) ||
      !!COLOURS[token] ||
      (token.length >= 4 && /\d/.test(token) && /[a-z]/.test(token))
    );
  }

  function distanceOne(a, b) {
    if (Math.abs(a.length - b.length) > 1) return false;
    var i = 0;
    var j = 0;
    var edits = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) {
        i += 1;
        j += 1;
      } else {
        edits += 1;
        if (edits > 1) return false;
        if (a.length > b.length) i += 1;
        else if (b.length > a.length) j += 1;
        else {
          i += 1;
          j += 1;
        }
      }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
  }

  function sameToken(a, b, joined) {
    if (a === b) return true;
    if (/\d/.test(a) || /\d/.test(b)) return false;
    var sa = stem(a);
    var sb = stem(b);
    if (sa === sb) return true;
    var short = sa.length <= sb.length ? sa : sb;
    var long = sa.length <= sb.length ? sb : sa;
    if (short.length >= 4 && long.indexOf(short) === 0 && short.length / long.length >= 0.6) return true;
    if (short.length >= 5 && distanceOne(sa, sb)) return true;
    if (a.length >= 6 && joined && joined.indexOf(a) > -1) return true;
    return false;
  }

  function hasToken(token, tokens, joined) {
    for (var i = 0; i < tokens.length; i += 1) {
      if (sameToken(token, tokens[i], joined)) return true;
    }
    return false;
  }

  /**
   * Compares what the visitor typed with one product. `product` carries title,
   * vendor, type and, when known, variants [{ id, title, sku, available }].
   * Returns null unless it is a close match. Dimensions and colours the visitor
   * named must be present on the product or one of its variants.
   */
  function scoreProduct(text, product, options) {
    var strictHard = !(options && options.hard === false);
    var wanted = significant(normalise(text));
    if (!wanted.length || !product || !product.title) return null;

    var titleTokens = significant(normalise(product.title));
    var baseTokens = titleTokens.concat(
      significant(normalise(product.vendor || "")),
      significant(normalise(product.type || "")),
    );
    var variants = product.variants && product.variants.length ? product.variants : [null];

    var best = null;
    variants.forEach(function (variant) {
      var extra = variant ? significant(normalise([variant.title, variant.sku].join(" "))) : [];
      var haystack = baseTokens.concat(extra);
      var joined = haystack.join("");
      var matched = 0;
      var hardMissing = false;
      wanted.forEach(function (token) {
        if (hasToken(token, haystack, joined)) matched += 1;
        else if (isHard(token)) hardMissing = true;
      });
      if (strictHard && hardMissing) return;

      var coverage = matched / wanted.length;
      var descriptive = titleTokens.filter(function (t) {
        return !DIMENSION.test(t);
      });
      var covered = descriptive.filter(function (t) {
        return hasToken(t, wanted, "");
      }).length;
      var precision = descriptive.length ? covered / descriptive.length : 0;

      // A size or length on the product that the visitor never mentioned means
      // we would be guessing which of several sizes they want.
      var productHasSize = titleTokens.some(function (t) {
        return DIMENSION.test(t);
      });
      var visitorGaveSize = wanted.some(function (t) {
        return DIMENSION.test(t);
      });
      if (productHasSize && !visitorGaveSize) return;

      var close =
        coverage >= 0.8 && precision >= 0.5 && (matched >= 3 || precision >= 0.75);
      if (!close) return;

      var score = coverage * 0.6 + precision * 0.4;
      var entry = {
        score: score,
        coverage: coverage,
        precision: precision,
        variantId: variant ? String(variant.id) : "",
        available: variant ? variant.available !== false : product.available !== false,
      };
      if (
        !best ||
        entry.score > best.score + 1e-9 ||
        (Math.abs(entry.score - best.score) < 1e-9 && entry.available && !best.available)
      ) {
        best = entry;
      }
    });
    return best;
  }

  /**
   * The single product to offer, or null. When two different products fit about
   * equally well the visitor has not said enough for us to guess, so nothing is offered.
   */
  function pickBest(text, products) {
    var scored = (products || [])
      .map(function (p) {
        return { product: p, result: scoreProduct(text, p) };
      })
      .filter(function (x) {
        return x.result;
      })
      .sort(function (a, b) {
        return b.result.score - a.result.score;
      });
    if (!scored.length) return null;
    if (scored.length > 1 && scored[0].result.score - scored[1].result.score < 0.04) return null;
    return scored[0];
  }

  /** Cheap first pass over predictive-search results: best few that could be close. */
  function shortlist(text, products, limit) {
    return (products || [])
      .map(function (p) {
        return { product: p, result: scoreProduct(text, p, { hard: false }) };
      })
      .filter(function (x) {
        return x.result;
      })
      .sort(function (a, b) {
        return b.result.score - a.result.score;
      })
      .slice(0, limit || 3)
      .map(function (x) {
        return x.product;
      });
  }

  var UNIT_WORDS =
    "boxes|box|rolls?|pcs|pieces?|units?|packs?|pairs?|sets?|reels?|drums?|lengths?|metres?|meters?|bags?|cases?|tubs?|tubes?";

  /** Splits "24 x Pro-Gaff black", "2 boxes AA", "gaffer tape x 24" or a CSV row into a quantity and a name. */
  function parseListLine(raw) {
    var line = String(raw || "")
      .replace(/^[\s\-•*·]+/, "")
      .trim();
    if (!line) return null;
    var m;
    if ((m = line.match(/^(\d{1,6})\s*(?:x|×|\*)\s*(.+)$/i))) return { qty: +m[1], title: m[2].trim() };
    if ((m = line.match(new RegExp("^(\\d{1,6})\\s+(?:" + UNIT_WORDS + ")\\s+(?:of\\s+)?(.+)$", "i")))) {
      return { qty: +m[1], title: m[2].trim() };
    }
    if ((m = line.match(/^(\d{1,6})\s*[,\t]\s*(.+)$/))) return { qty: +m[1], title: m[2].trim() };
    if ((m = line.match(/^(.+?)\s*[,\t]\s*(\d{1,6})\s*$/))) return { qty: +m[2], title: m[1].trim() };
    if ((m = line.match(/^(.+?)\s+(?:x|×|\*)\s*(\d{1,6})\s*$/i))) return { qty: +m[2], title: m[1].trim() };
    if ((m = line.match(/^(\d{1,6})\s+([a-z].*)$/i))) return { qty: +m[1], title: m[2].trim() };
    return { qty: 1, title: line };
  }

  return {
    normalise: normalise,
    scoreProduct: scoreProduct,
    shortlist: shortlist,
    pickBest: pickBest,
    parseListLine: parseListLine,
  };
});
